import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import startcase from 'lodash.startcase';
import { device } from '../../utils/styled-component-mixins';
import { useLocalization } from '../custom-hooks/useLocalization';
import EventWebinarFormattedTime from './cards/EventWebinarCard/EventWebinarFormattedTime';
import { withLocalization } from '../shared/Context/LocalizationContext';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import { compose } from '../utils/compose';

const FeaturedEventWrapper = styled.div`
  display: flex;

  .featured-event {
    display: inline-flex;
    box-shadow: var(--card-shadow);
    width: 100%;

    .event-thumbnail {
      background-image: url('${(props) => props.thumbnail}');
      background-repeat: no-repeat;
      background-size: cover;
      width: 50%;
      object-fit: cover;

      @media ${device.mobileOnly} {
        display: none;
      }
    }

    .event-details {
      width: 50%;

      @media ${device.mobileOnly} {
        width: 100%;
      }
    }

    .featured-card-content {
      background-color: var(--white);
      padding: 4.8rem 1.6rem 4.8rem 0.7rem;
      z-index: 100;

      @media ${device.desktop} {
        padding-right: 4rem;
      }

      @media ${device.mobileOnly} {
        padding-bottom: 3.1rem;
      }

      div[class*='WidgetIdWrapper'] {
        height: auto;
      }
    }

    .event-column-wrapper {
      column-gap: 1.5rem;
      display: flex;
      height: 100%;
      position: relative;
      z-index: 100;

      .event-right-column {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .right-column-content {
          margin-top: -0.4rem;
        }

        h3 {
          font-size: 1.6rem;
          font-weight: 700;
          line-height: 2.4rem;
          margin: 0 0 1.6rem 0;

          @media ${device.desktop} {
            font-size: 2.2rem;
            font-weight: 800;
            line-height: 3.2rem;
            margin: 0 0 0.8rem 0;
          }
        }

        p {
          display: none;

          @media ${device.desktop} {
            display: block;
            font-size: 1.4rem;
          }
        }

        .btn-wrapper {
          margin-top: 2rem;

          a {
            display: flex;
          }

          span {
            &:first-child {
              font-size: 1.4rem;
              line-height: 1.8rem;
            }

            @media ${device.desktop} {
              text-transform: uppercase;

              &:last-child {
                font-size: 1.6rem;
                line-height: 2.4rem;
              }
            }
          }

          .material-icons-round {
            border-bottom: 2px solid transparent;
            font-size: 1.8rem;
            margin-left: 0.5rem;
          }
        }

        time {
          display: block;
        }
      }
    }

    .overflow-wrapper {
      overflow: hidden;
      position: relative;
      z-index: 100;
    }

    .tag {
      display: inline-block;
      margin-bottom: 1.6rem;
    }

    .event-date {
      display: block;
      margin-top: -0.9rem;
      width: 6.6rem;

      .event-day {
        font-size: 3rem;
        line-height: 4rem;
      }

      > span {
        color: var(--charcoal-2);
        display: block;
        font-weight: 800;
        text-align: center;
      }

      span:not(.event-day) {
        font-size: 1.4rem;
        line-height: 2rem;
      }
    }

    .event-time-location {
      margin-bottom: 1.6rem;
    }

    address,
    time {
      font-size: 1.4rem;
      font-style: normal;
      line-height: 2.4rem;
    }

    .featured-card-content {
      .publish-date {
        color: var(--charcoal-2);
        display: block;
        font-size: 1.2rem;
        letter-spacing: 0.07rem;
        line-height: 1.6rem;
        margin-bottom: 3rem;
        text-transform: uppercase;
      }
    }

    .overflow-wrapper > a {
      align-items: center;
      color: var(--cta-blue);
      display: flex;
      font-size: 1.4rem;
      font-weight: 700;
      line-height: 1.8rem;
      margin-top: 1rem;
      text-decoration: none;

      span:last-child {
        font-size: 3.2rem;
      }

      @media ${device.tablet} {
        transition: all 0.3s ease-in-out;

        span:first-child {
          transition: all 0.3s ease-in-out;
        }
      }
    }
  }
`;

const FeaturedEventComponent = (props) => {
  const isMobile = useMediaQuery(device.mobileOnly);
  const { resourceStrings } = useLocalization();
  const [thumbnail, setThumbnail] = useState('');
  const { city, state, dateFrom, description, pageTitle, slug, timeZone } = props.event || {};
  const eventTypes = {
    0: resourceStrings['bdO.EventsWebinars.Event'],
    1: resourceStrings['bdO.EventsWebinars.Webinar'],
    2: resourceStrings['bdO.EventsWebinars.PastWebinar'],
    default: resourceStrings['bdO.EventsWebinars.PastWebinar']
  };
  const eventType = eventTypes[props?.event?.eventType] || eventTypes.default;
  const formatTime = (isoDate) => DateTime.fromISO(isoDate).toFormat('t');
  const linkText = props.properties.linkText || resourceStrings['bdO.FeaturedEvent.LinkText'];

  useEffect(() => {
    setThumbnail(props.properties.eventThumbnail);
  }, [props.properties.eventThumbnail]);

  return (
    <WidgetIdWrapper widgetID={props.properties.widgetID}>
      <FeaturedEventWrapper thumbnail={thumbnail}>
        <div className="featured-event">
          <div className="event-thumbnail"></div>
          <div className="event-details">
            {props.isFeatured && <div className="featured-tag">{props.featuredText}</div>}
            <div className="featured-card-content">
              <div>
                <div className="event-column-wrapper">
                  <div className="event-left-column">
                    <time className="event-date" dateTime={dateFrom}>
                      <EventWebinarFormattedTime isoDate={dateFrom} />
                    </time>
                  </div>
                  <div className="event-right-column">
                    <div className="right-column-content">
                      {eventType && <span className="tag">{startcase(eventType)}</span>}
                      <div className="event-time-location">
                        <time dateTime={formatTime(dateFrom)} suppressHydrationWarning>
                          {formatTime(dateFrom)} {timeZone}
                        </time>
                        {city && state && (
                          <address>
                            <span>
                              {city}, {state}
                            </span>
                          </address>
                        )}
                      </div>
                      <h3>{pageTitle}</h3>
                      <p dangerouslySetInnerHTML={{ __html: description }}></p>
                      {isMobile ? (
                        <ButtonLink text={linkText} link={slug} type="tertiary" theme="light" />
                      ) : (
                        <ButtonLink
                          text={linkText}
                          link={slug}
                          type="primary"
                          theme="light"
                          size="medium"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeaturedEventWrapper>
    </WidgetIdWrapper>
  );
};

export default compose(withLocalization, withWidgetSpacingWrapper)(FeaturedEventComponent);
